export const djEquipmentRentalFallback = {
  page: {
    path: '/dj-equipment-rental',
    canonicalUrl: 'https://miamisoundrental.com/dj-equipment-rental'
  },
  revision: {
    seo: {
      title: 'DJ Equipment Rental Miami | CDJs, Mixers & Setups',
      description: 'Rent DJ equipment in Miami and Fort Lauderdale. Compare CDJs, mixers, controllers, turntables, booths and complete setups with pickup or delivery.',
      canonicalUrl: 'https://miamisoundrental.com/dj-equipment-rental',
      robots: {
        index: true,
        follow: true
      },
      primaryKeyword: 'DJ equipment rental Miami',
      secondaryKeywords: [
        'DJ equipment rental Fort Lauderdale',
        'DJ gear rental Miami',
        'DJ setup rental Miami',
        'DJ system rental Miami',
        'CDJ and mixer rental Miami',
        'professional DJ equipment rental',
        'DJ equipment rental near me'
      ],
      searchIntent: 'mixed',
      entities: [
        'Miami Sound Rental',
        'DJ equipment rental',
        'Pioneer CDJ',
        'DJ mixer',
        'DJ controller',
        'turntable',
        'DJ booth'
      ],
      targetLocations: ['Miami', 'Fort Lauderdale', 'Palm Beach', 'South Florida'],
      openGraph: {
        type: 'website',
        url: 'https://miamisoundrental.com/dj-equipment-rental',
        title: 'DJ Equipment Rental Miami | CDJs, Mixers & Setups',
        description: 'Compare complete DJ setups, CDJs, mixers, controllers, turntables and booths with Miami pickup or South Florida delivery.',
        images: [{
          url: 'https://miamisoundrental.com/Pictures/categories-v2/dj-equipment-rental-hero-v2.jpg',
          width: 1915,
          height: 821,
          alt: 'Professional DJ mixer, media players, and monitor speaker at a Miami event venue'
        }]
      },
      twitter: {
        cardType: 'summary_large_image'
      }
    },
    content: {
      hero: {
        eyebrow: 'DJ equipment rental · Miami, Fort Lauderdale & South Florida',
        title: 'DJ Equipment Rental',
        description: 'Choose a club-standard CDJ setup, a DJ controller, turntables, a booth, or individual components. Compare complete setups online or ask us to confirm the rider, connections, pickup, delivery, and setup.',
        image: '/Pictures/categories-v2/dj-equipment-rental-hero-v2.jpg',
        imageAlt: 'Professional DJ mixer, media players, and monitor speaker at a Miami event venue',
        imagePosition: '65% center',
        primaryAction: 'Choose Your DJ Setup',
        secondaryAction: 'Check Setup Compatibility',
        tertiaryAction: 'Call 305 974 4411'
      },
      process: {
        label: 'How DJ equipment rental works',
        steps: [
          { icon: 'search', title: 'Choose the workflow', description: 'Club-standard, controller, or vinyl.' },
          { icon: 'calendar', title: 'Compare the setup', description: 'Review models, contents, and pricing.' },
          { icon: 'truck', title: 'Select fulfillment', description: 'Choose Miami pickup or delivery and setup.' },
          { icon: 'success', title: 'Confirm compatibility', description: 'Verify the rider, connections, and signal path.' }
        ]
      },
      marketNotice: {
        ariaLabel: 'Miami and Orlando DJ equipment service areas',
        heading: 'Need DJ equipment in Orlando or Tampa?',
        body: 'Central Florida inventory, pickup, delivery, and Tampa requests are handled through the Orlando DJ Equipment Rental page.',
        cta: { label: 'View Orlando DJ Equipment', url: '/orlando/dj-equipment-rental' },
        conversionKey: 'dj-equipment-rental.market.orlando',
        conversionLocation: 'after-hero'
      },
      workflow: {
        eyebrow: 'DJ setup decision guide',
        title: 'Choose the playback workflow before individual models',
        description: 'The right starting point depends on how the performer prepares and plays music, the technical rider, and the venue signal path—not simply which product is newest.',
        items: [
          {
            key: 'club',
            label: 'USB and club-standard workflow',
            title: 'CDJs or media players with a DJ mixer',
            icon: 'disc',
            summary: 'Use separate players and a mixer when the performer expects a club-style layout or provides an exact-model rider.',
            points: ['Confirm the exact player and mixer models', 'Check Rekordbox export compatibility', 'Match player count to mixer channels'],
            href: '/dj-equipment/cdjs',
            actionLabel: 'Compare CDJs',
            conversionKey: 'dj-equipment-rental.workflow.cdjs'
          },
          {
            key: 'controller',
            label: 'Laptop-based workflow',
            title: 'DJ controller and performance software',
            icon: 'sliders',
            summary: 'Choose a controller when the DJ performs from a laptop and has confirmed the supported software, computer connection, and model.',
            points: ['Confirm Rekordbox or Serato support', 'Check laptop ports and adapters', 'Plan a backup playback path'],
            href: '/dj-equipment/controller',
            actionLabel: 'Compare DJ Controllers',
            conversionKey: 'dj-equipment-rental.workflow.controllers'
          },
          {
            key: 'vinyl',
            label: 'Vinyl or hybrid rider',
            title: 'Turntables with a compatible mixer',
            icon: 'refresh',
            summary: 'Use turntables for vinyl playback or a rider that specifically requests them, with the correct cartridges, mixer inputs, and isolated surface.',
            points: ['Confirm vinyl or timecode use', 'Specify cartridges and slipmats', 'Keep decks isolated from vibration'],
            href: '/dj-equipment/turntables',
            actionLabel: 'Compare Turntables',
            conversionKey: 'dj-equipment-rental.workflow.turntables'
          }
        ]
      },
      introduction: {
        eyebrow: 'Plan the complete playback system',
        title: 'Start with the performer workflow, then confirm every connection',
        paragraphs: [
          'This page is the broad South Florida gateway for professional DJ equipment. Use it when you are choosing a workflow or complete setup. If the rider already names a CDJ, mixer, controller, turntable, or booth, use the focused category so the exact equipment remains easy to compare.',
          'Configured DJ setups are shown before individual components because most historical orders combine several explicit products or a configured bundle. Standalone rentals remain available when one component is genuinely all you need.'
        ],
        guideHeading: 'What must be confirmed before booking?',
        guideSummary: 'A model list is only the start of a performance-ready DJ setup.',
        guideParagraphs: [
          'Confirm the performer rider, player quantity, mixer channels, laptop or USB workflow, booth position, monitors, audience sound, cable distance, network needs, power, and setup responsibilities.',
          'Every product and bundle lists what is included. Speakers, microphones, booths, cables, delivery, setup, and a performing DJ are not implied unless the selected offer explicitly includes them.'
        ],
        image: '/Pictures/Product/dj-equipment-rental-miami.jpg',
        imageAlt: 'DJ performing behind a booth with a professional sound system at a real Miami event',
        signals: [
          'Complete DJ setup choices appear before individual components',
          'Exact-model intent is routed to focused equipment pages',
          'Pickup, delivery, setup, and included items stay product-specific'
        ]
      },
      setups: {
        eyebrow: 'Configured DJ setup rentals',
        title: 'Start with a complete DJ setup',
        description: 'These configured products combine compatible playback components into a clearer starting point. Review every included item, choose dates, and confirm whether pickup or delivery and setup is available for that specific setup.',
        helpHeading: 'Need help checking a DJ rider or setup?',
        helpDescription: 'Share the requested models, playback method, performer inputs, speaker connection, booth position, power, and venue. We will identify conflicts or missing pieces before the event.',
        helpAction: 'Check Setup Compatibility'
      },
      specialists: {
        eyebrow: 'Exact equipment categories',
        title: 'Already know what the rider requires?',
        description: 'Use the focused page for exact CDJ, mixer, controller, turntable, or booth demand. This keeps model comparisons clear while the broad gateway remains available for complete setups.',
        items: [
          { title: 'CDJ & Media Player Rental', href: '/dj-equipment/cdjs', description: 'Compare Pioneer CDJs, media players, exact models, availability, and compatible mixer paths.', conversionKey: 'dj-equipment-rental.specialist.cdjs' },
          { title: 'DJ Mixer Rental', href: '/dj-equipment/mixers', description: 'Compare channel count, inputs, effects, rider models, and club-standard mixer options.', conversionKey: 'dj-equipment-rental.specialist.mixers' },
          { title: 'DJ Controller Rental', href: '/dj-equipment/controller', description: 'Compare laptop-based controllers by software support, connections, and performer workflow.', conversionKey: 'dj-equipment-rental.specialist.controllers' },
          { title: 'Turntable Rental', href: '/dj-equipment/turntables', description: 'Compare vinyl and timecode options, cartridges, isolation, and compatible mixers.', conversionKey: 'dj-equipment-rental.specialist.turntables' },
          { title: 'DJ Booth Rental', href: '/dj-equipment/booth', description: 'Compare portable booths, facades, screens, table requirements, and equipment footprint.', conversionKey: 'dj-equipment-rental.specialist.booths' }
        ]
      },
      components: {
        eyebrow: 'Individual DJ equipment rentals',
        title: 'Rent a specific component when that is the complete job',
        description: 'Standalone CDJs, mixers, controllers, turntables, booths, and effects remain available. Confirm compatibility with the rest of the performer and venue system before reserving a single component.'
      },
      boundary: {
        eyebrow: 'Complete the signal path',
        title: 'DJ playback equipment and audience sound are separate decisions',
        paragraphs: [
          'CDJs, controllers, turntables, and mixers create the performer playback system. The audience still needs speakers and, when appropriate, subwoofers selected for the room, crowd, program level, and coverage distance.',
          'Tell us what the performer brings, where the booth sits, which inputs the sound system accepts, how far cables must run, and whether a monitor is required. We can coordinate the full path without implying that every item is included in the DJ rental.'
        ],
        secondaryHeading: 'Need speakers or a complete PA system too?',
        secondaryParagraph: 'Choose the Audio-family page that matches the remaining job:',
        image: '/Pictures/Product/dj-booth-rental-miami.jpg',
        imageAlt: 'DJ booth, audience speakers, lighting, and live performers at a real Miami event'
      },
      events: {
        eyebrow: 'Recent production work',
        title: 'DJ equipment working inside real event systems',
        description: 'Documented Miami Sound Rental projects show how playback equipment, booths, audience sound, lighting, video, power, and crew change from one event to another.'
      },
      reviews: {
        eyebrow: 'Verified Google feedback',
        title: 'Recent feedback about equipment and event support',
        description: 'Showing verified Google feedback selected for equipment-rental, event-service, DJ, and South Florida relevance when available.'
      },
      faqs: [
        { question: 'What is included with a DJ equipment rental?', answer: 'Included items vary by product and configured setup. The product row lists the rental item and any included components. Speakers, microphones, booths, cables, power distribution, delivery, setup, and a performing DJ are not included unless the selected offer explicitly says so.' },
        { question: 'Should I rent CDJs, a DJ controller, or turntables?', answer: 'Choose CDJs and a club-style mixer for USB playback and club-standard technical riders. Choose a controller for a laptop-based Rekordbox or Serato workflow after confirming the exact model and computer connection. Choose turntables for vinyl, timecode, or a rider that specifically requires them.' },
        { question: 'Can I rent a complete DJ setup instead of choosing every component?', answer: 'Yes. Configured DJ setup products are shown first on this page. Review the included players, mixer, cables, accessories, and booth-related items, then confirm any audience sound, monitoring, power, or delivery requirements that are still separate.' },
        { question: 'Can I rent one CDJ, mixer, controller, turntable, or DJ booth by itself?', answer: 'Yes, eligible individual components can be rented separately when that is the full requirement. Use the focused category page for exact-model comparisons, and confirm that the component is compatible with the equipment the performer or venue will provide.' },
        { question: 'Are speakers and subwoofers included with DJ equipment?', answer: 'Not unless the selected product or setup specifically lists them. DJ playback equipment and audience sound solve different jobs. Use the Speaker Rental page for individual speakers or the Sound System Rental page for a complete PA recommendation.' },
        { question: 'Do you provide delivery, setup, and technical support?', answer: 'Yes. Delivery and setup are available across Miami, Fort Lauderdale, Palm Beach, and South Florida. Some products permit Miami warehouse pickup; others require professional delivery or installation. The available fulfillment path is shown for each product before checkout.' },
        { question: 'Can I pick up DJ equipment from the Miami warehouse?', answer: 'Pickup is available only for eligible products and setups. Choose the product and dates to see the applicable fulfillment options. Products that require a technician or installation will show delivery and setup instead of warehouse pickup.' },
        { question: 'Can you check a performer technical rider before I book?', answer: 'Yes. Send the rider, exact model requests, player quantity, mixer, software or media format, booth position, monitor needs, audience-sound connection, power, and venue schedule. The team can identify substitutions, conflicts, and missing items before the order is finalized.' },
        { question: 'Does the rental include a DJ performer?', answer: 'No. This page rents professional DJ equipment and setup support. It does not imply that a performing DJ is included. If you need entertainment talent as well as equipment, tell the team so that request can be reviewed separately.' },
        { question: 'Do you serve Orlando and Tampa from this page?', answer: 'No. This page represents the Miami and South Florida service area. Use the Orlando DJ Equipment Rental page for Orlando inventory, Central Florida pickup or delivery, and Tampa requests handled by the Orlando team.' }
      ],
      related: {
        eyebrow: 'Related event systems',
        title: 'Add the services that complete the event—not the DJ keyword target',
        description: 'Use the appropriate family owner for audience sound, lighting, or managed production after the playback workflow is clear.',
        items: [
          { title: 'Sound System Rental', href: '/sound-system-rental', description: 'Plan a complete PA system for audience coverage, microphones, subwoofers, setup, and technical support.' },
          { title: 'Speaker Rental', href: '/speaker-rental', description: 'Compare individual powered speakers, subwoofers, and straightforward audio bundles.' },
          { title: 'Lighting Rental', href: '/lighting-rental', description: 'Add stage lighting, moving lights, uplighting, and effects through the Lighting family.' },
          { title: 'Corporate Event Production', href: '/corporate-event-production', description: 'Use a managed production path when DJ playback is one part of a larger audio, video, lighting, staging, and crew scope.' }
        ]
      },
      finalCta: {
        eyebrow: 'Choose the next step',
        title: 'Ready to reserve the right DJ setup?',
        description: 'Compare configured DJ setups online, or ask a specialist to confirm the rider, exact models, connections, audience sound, pickup, delivery, and setup.',
        primaryAction: 'Compare Complete DJ Setups',
        secondaryAction: 'Check Setup Compatibility',
        proofItems: ['Configured setups shown before individual components', 'Standalone rentals remain available', 'Fulfillment and included items stay product-specific']
      }
    }
  }
}

export default djEquipmentRentalFallback
